import React, { useState, useEffect, useRef } from "react";
import {
  importMutualFunds, importMutualFundsPdf, importUSStocks, getImportHistory,
  getUSStockAccounts, previewMfCentralCas, applyMfCentralCas, getMfCentralBatches,
  getMfCentralEmailSources, saveMfCentralEmailSource, checkStatementEmail,
} from "../lib/api";
import { Upload, FileSpreadsheet, FileText, Check, AlertCircle, Loader2, Lock, Download, ShieldCheck, Mail, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { importErrorMessage } from "../lib/importErrors";

export default function ImportPage() {
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [importType, setImportType] = useState("mfcentral-cas");
  const [mode, setMode] = useState("replace");
  const [pdfPassword, setPdfPassword] = useState("");
  const [pdfResult, setPdfResult] = useState(null);
  const [usResult, setUsResult] = useState(null);
  const [usAccounts, setUsAccounts] = useState([]);
  const [selectedUsAccount, setSelectedUsAccount] = useState("");
  const [selectedMfOwner, setSelectedMfOwner] = useState("aman");
  const [applyingCas, setApplyingCas] = useState(false);
  const [casBatches, setCasBatches] = useState([]);
  const [mfEmailSources, setMfEmailSources] = useState([]);
  const [mfEmailSender, setMfEmailSender] = useState("");
  const [mfEmailSubject, setMfEmailSubject] = useState("Consolidated Account Statement");
  const [mfEmailFilename, setMfEmailFilename] = useState(".pdf");
  const [mfEmailPassword, setMfEmailPassword] = useState("");
  const [savingMfEmail, setSavingMfEmail] = useState(false);
  const [checkingMfEmail, setCheckingMfEmail] = useState(false);
  const [mfEmailMessage, setMfEmailMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef();

  const loadMfCentral = async () => {
    try {
      const [sourcesRes, batchesRes] = await Promise.all([
        getMfCentralEmailSources(),
        getMfCentralBatches(),
      ]);
      setMfEmailSources(sourcesRes.data.sources || []);
      setCasBatches(batchesRes.data.batches || []);
    } catch (_) {}
  };

  useEffect(() => {
    getImportHistory().then(r => setHistory(r.data.history || [])).catch(() => {});
    getUSStockAccounts().then(r => {
      const accs = r.data.accounts || [];
      setUsAccounts(accs);
      if (accs.length > 0 && !selectedUsAccount) setSelectedUsAccount(accs[0].id);
    }).catch(() => {});
    loadMfCentral();
  }, [result]); // eslint-disable-line

  useEffect(() => {
    const source = mfEmailSources.find(item => item.owner_id === selectedMfOwner);
    setMfEmailSender(source?.sender_aliases?.[0] || "");
    setMfEmailSubject(source?.subject_contains?.[0] || "Consolidated Account Statement");
    setMfEmailFilename(source?.filename_contains?.[0] || ".pdf");
    setMfEmailPassword("");
  }, [selectedMfOwner, mfEmailSources]);

  const handleUpload = async () => {
    const file = selectedFile || fileRef.current?.files?.[0];
    if (!file) {
      setResult({
        success: false,
        message: importType === "mfcentral-cas"
          ? "Choose a PDF before previewing the CAS."
          : "Choose a file before importing.",
      });
      return;
    }
    setUploading(true);
    setResult(null);
    setPdfResult(null);
    setUsResult(null);
    try {
      let data;
      if (importType === "mfcentral-cas") {
        if (!pdfPassword) {
          setResult({ success: false, message: "CAS PDF password is required" });
          setUploading(false);
          return;
        }
        const res = await previewMfCentralCas(file, pdfPassword, selectedMfOwner);
        data = res.data;
        setPdfResult(data);
      } else if (importType === "mutual-funds-pdf") {
        if (!pdfPassword) {
          setResult({ success: false, message: "PDF password is required" });
          setUploading(false);
          return;
        }
        const res = await importMutualFundsPdf(file, pdfPassword, mode);
        data = res.data;
        setPdfResult(data);
      } else if (importType === "mutual-funds") {
        const res = await importMutualFunds(file, mode);
        data = res.data;
      } else {
        const res = await importUSStocks(file, mode, selectedUsAccount);
        data = res.data;
        setUsResult(data);
      }
      setResult({ success: true, message: data.message });
      if (fileRef.current) fileRef.current.value = "";
      setSelectedFile(null);
    } catch (err) {
      setResult({ success: false, message: importErrorMessage(err) });
    } finally {
      setUploading(false);
    }
  };

  const handleApplyCas = async () => {
    if (!pdfResult?.id || pdfResult.status !== "Ready for Review") return;
    setApplyingCas(true);
    try {
      const res = await applyMfCentralCas(pdfResult.id);
      setPdfResult(res.data);
      setResult({ success: true, message: res.data.message });
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || "Could not apply CAS" });
    } finally {
      setApplyingCas(false);
    }
  };

  const handleSaveMfEmail = async () => {
    if (!mfEmailSender.trim() || !mfEmailSubject.trim()) {
      setMfEmailMessage({ success: false, text: "Enter the CAS sender and subject text." });
      return;
    }
    setSavingMfEmail(true);
    setMfEmailMessage(null);
    try {
      await saveMfCentralEmailSource({
        owner_id: selectedMfOwner,
        sender_aliases: [mfEmailSender.trim()],
        subject_contains: [mfEmailSubject.trim()],
        filename_contains: mfEmailFilename.trim() ? [mfEmailFilename.trim()] : [],
        pdf_password: mfEmailPassword || null,
        active: true,
      });
      setMfEmailMessage({ success: true, text: "CAMS/KFintech e-CAS pickup saved." });
      await loadMfCentral();
    } catch (err) {
      setMfEmailMessage({ success: false, text: err.response?.data?.detail || "Could not save automatic pickup." });
    } finally {
      setSavingMfEmail(false);
    }
  };

  const handleCheckMfEmail = async () => {
    setCheckingMfEmail(true);
    setMfEmailMessage(null);
    try {
      const res = await checkStatementEmail();
      const staged = res.data.attachments_staged || res.data.staged?.length || 0;
      setMfEmailMessage({
        success: true,
        text: staged > 0
          ? `${staged} new file${staged === 1 ? "" : "s"} found and staged for review.`
          : "Gmail checked. No new matching CAS was found.",
      });
      await loadMfCentral();
    } catch (err) {
      setMfEmailMessage({ success: false, text: err.response?.data?.detail || "Gmail could not be checked." });
    } finally {
      setCheckingMfEmail(false);
    }
  };

  const isPdf = importType === "mutual-funds-pdf" || importType === "mfcentral-cas";
  const isMfCentral = importType === "mfcentral-cas";
  const isUS = importType === "us-stocks";
  const acceptTypes = isPdf ? ".pdf" : isUS ? ".csv,.xlsx,.xls,.pdf" : ".csv";
  const selectedMfSource = mfEmailSources.find(item => item.owner_id === selectedMfOwner);

  const downloadTemplate = () => {
    let csv, filename;
    if (importType === "us-stocks") {
      csv = "symbol,quantity,average_price,current_price\nAAPL,10,150.00,178.50\nMSFT,5,320.00,415.25\nGOOGL,3,140.00,175.80\nAMZN,8,155.00,195.60\nNVDA,2,450.00,890.00";
      filename = "us_stocks_template.csv";
    } else {
      csv = "scheme_name,units,average_nav,current_nav,category,isin\nAxis Liquid Fund - Direct Growth,100,2500.00,3049.47,Debt - Liquid,INF846K01CX4\nHDFC Mid-Cap Fund - Growth,500,45.50,52.80,Equity - Mid Cap,INF179K01234";
      filename = "mutual_funds_template.csv";
    }
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Import Portfolio Data</h1>
        <p className="text-sm text-[var(--text-muted)]">Upload CDSL CAS PDF or CSV files for Mutual Funds and US Stocks</p>
      </div>

      {/* Upload Card */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-6" data-testid="upload-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">Import Type</label>
            <Select value={importType} onValueChange={(v) => {
              setImportType(v); setResult(null); setPdfResult(null); setUsResult(null); setSelectedFile(null);
              if (fileRef.current) fileRef.current.value = "";
            }}>
              <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="import-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                <SelectItem value="mfcentral-cas">Mutual Fund CAS — Review & Apply</SelectItem>
                <SelectItem value="mutual-funds-pdf">Legacy CDSL CAS — Direct Import</SelectItem>
                <SelectItem value="mutual-funds">Mutual Funds CSV</SelectItem>
                <SelectItem value="us-stocks">US Stocks (CSV / XLSX / PDF)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isMfCentral && (
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">Mode</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="import-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                <SelectItem value="replace">Replace Existing</SelectItem>
                <SelectItem value="append">Append to Existing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}
          {isMfCentral && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">Portfolio Owner</label>
              <Select value={selectedMfOwner} onValueChange={setSelectedMfOwner}>
                <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="mf-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                  <SelectItem value="aman">Aman Saluja</SelectItem>
                  <SelectItem value="gurpreet">Gurpreet Kaur</SelectItem>
                  <SelectItem value="anya">Anya Saluja</SelectItem>
                  <SelectItem value="ayza">Ayza Saluja</SelectItem>
                  <SelectItem value="aan">Aan Saluja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {isPdf && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">PDF Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <Input
                  type="password"
                  value={pdfPassword}
                  onChange={e => setPdfPassword(e.target.value)}
                  placeholder="Enter PAN / password"
                  className="bg-[var(--bg-base)] border-white/10 pl-9 text-sm"
                  data-testid="pdf-password-input"
                />
              </div>
            </div>
          )}
          {isUS && usAccounts.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">US Account</label>
              <Select value={selectedUsAccount} onValueChange={setSelectedUsAccount}>
                <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="us-account-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                  {usAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Dropzone */}
        <label className="border-2 border-dashed border-white/20 hover:border-[var(--brand)] bg-[var(--bg-base)] hover:bg-[var(--bg-elevated)] rounded-md p-8 flex flex-col items-center justify-center cursor-pointer transition-all" data-testid="file-dropzone">
          {isPdf ? <FileText size={32} className="text-[var(--text-muted)] mb-3" /> : <Upload size={32} className="text-[var(--text-muted)] mb-3" />}
          <span className="text-sm text-[var(--text-secondary)]">
            {selectedFile
              ? selectedFile.name
              : `Click to upload ${isMfCentral ? "MF Central download or CAMS / KFintech e-CAS PDF" : isPdf ? "CDSL CAS PDF" : isUS ? "US holdings (CSV / XLSX / PDF)" : "CSV"} file`}
          </span>
          <span className="text-xs text-[var(--text-muted)] mt-1">
            {selectedFile
              ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB selected — ${isMfCentral ? "enter the PDF password, then press Preview CAS" : "ready to import"}`
              : isPdf
              ? isMfCentral
                ? "The portfolio is unchanged until you review the totals and press Apply"
                : "Monthly CAS statement from CDSL (password-protected)"
              : isUS
                ? "Vested (.xlsx) | INDmoney/Alpaca (.pdf) | Generic CSV"
                : "Columns: scheme_name, units, average_nav, current_nav, category"}
          </span>
          <input
            ref={fileRef}
            type="file"
            accept={acceptTypes}
            className="hidden"
            onChange={event => {
              setSelectedFile(event.target.files?.[0] || null);
              setResult(null);
              setPdfResult(null);
            }}
          />
        </label>

        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <Button onClick={handleUpload} disabled={uploading} className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded" data-testid="upload-btn">
            {uploading
              ? <><Loader2 size={16} className="animate-spin mr-1" />Processing...</>
              : <><FileSpreadsheet size={16} className="mr-1" />{isMfCentral ? "Preview CAS" : "Import"}</>}
          </Button>
          {!isPdf && (
            <Button variant="outline" onClick={downloadTemplate} className="border-white/10 text-[var(--text-secondary)] hover:text-white rounded text-xs" data-testid="download-template-btn">
              <Download size={14} className="mr-1" />Download Template
            </Button>
          )}
          {result && (
            <div className={`flex items-center gap-1.5 text-sm ${result.success ? "text-[var(--positive)]" : "text-[var(--negative)]"}`} data-testid="upload-result">
              {result.success ? <Check size={16} /> : <AlertCircle size={16} />}
              {result.message}
            </div>
          )}
        </div>
      </div>

      {isMfCentral && (
        <div className="bg-cyan-500/[0.04] border border-cyan-400/20 rounded-md p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-cyan-300 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-white">Safe mutual-fund CAS import</div>
              <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">
                Download a CAS directly from MF Central, or upload the password-protected e-CAS delivered by CAMS or KFintech.
                Capital360 detects repeat files, previews every scheme and total, and changes the portfolio only after approval.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                <a className="text-cyan-300 hover:text-cyan-200" href="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement" target="_blank" rel="noreferrer">Request CAMS e-CAS</a>
                <a className="text-cyan-300 hover:text-cyan-200" href="https://mfs.kfintech.com/investor/General/ConsolidatedAccountStatement" target="_blank" rel="noreferrer">Request KFintech e-CAS</a>
                <a className="text-cyan-300 hover:text-cyan-200" href="https://www.amfiindia.com/online-center/download-cas" target="_blank" rel="noreferrer">AMFI guidance</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMfCentral && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
            <div className="flex items-start gap-3">
              <Mail size={20} className="text-cyan-300 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-white">Automatic CAMS / KFintech e-CAS pickup from Gmail</div>
                <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed max-w-2xl">
                  MF Central generates a download and does not email it. For automatic pickup, request a Detailed
                  e-CAS from CAMS or KFintech. Capital360 reads only matching emails, stages each new PDF for review,
                  and never changes the portfolio automatically.
                </p>
              </div>
            </div>
            <div className={`text-[10px] uppercase tracking-wider ${
              selectedMfSource?.active ? "text-emerald-300" : "text-[var(--text-muted)]"
            }`}>
              {selectedMfSource?.active ? "Automatic pickup active" : "Not configured"}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">
                CAMS / KFintech e-CAS sender
              </label>
              <Input
                value={mfEmailSender}
                onChange={event => setMfEmailSender(event.target.value)}
                placeholder="Exact sender name or email address"
                className="bg-[var(--bg-base)] border-white/10"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">
                e-CAS subject contains
              </label>
              <Input
                value={mfEmailSubject}
                onChange={event => setMfEmailSubject(event.target.value)}
                placeholder="Consolidated Account Statement"
                className="bg-[var(--bg-base)] border-white/10"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">
                Attachment name contains
              </label>
              <Input
                value={mfEmailFilename}
                onChange={event => setMfEmailFilename(event.target.value)}
                placeholder=".pdf"
                className="bg-[var(--bg-base)] border-white/10"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5 block">
                e-CAS PDF password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <Input
                  type="password"
                  value={mfEmailPassword}
                  onChange={event => setMfEmailPassword(event.target.value)}
                  placeholder={selectedMfSource?.has_pdf_password ? "Saved securely — leave blank to keep it" : "Enter the PDF password"}
                  className="bg-[var(--bg-base)] border-white/10 pl-9"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mt-4">
            <Button
              onClick={handleSaveMfEmail}
              disabled={savingMfEmail}
              className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white"
            >
              {savingMfEmail && <Loader2 size={15} className="animate-spin mr-1" />}
              Save automatic pickup
            </Button>
            <Button
              variant="outline"
              onClick={handleCheckMfEmail}
              disabled={checkingMfEmail || !selectedMfSource?.active}
              className="border-white/10 text-[var(--text-secondary)] hover:text-white"
            >
              <RefreshCw size={15} className={`mr-1 ${checkingMfEmail ? "animate-spin" : ""}`} />
              Check Gmail now
            </Button>
            {mfEmailMessage && (
              <div className={`flex items-center gap-1.5 text-xs ${
                mfEmailMessage.success ? "text-[var(--positive)]" : "text-[var(--negative)]"
              }`}>
                {mfEmailMessage.success ? <Check size={14} /> : <AlertCircle size={14} />}
                {mfEmailMessage.text}
              </div>
            )}
          </div>
        </div>
      )}

      {isMfCentral && casBatches.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5">
          <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-3">
            Recent CAS files
          </div>
          <div className="space-y-2">
            {casBatches.slice(0, 8).map(batch => (
              <button
                type="button"
                key={batch.id}
                onClick={() => setPdfResult(batch)}
                className="w-full text-left flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 bg-[var(--bg-base)] border border-white/5 hover:border-cyan-400/30 rounded transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{batch.file_name}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {batch.owner_name} · {batch.records} schemes
                    {batch.statement_period && ` · ${batch.statement_period}`}
                    {batch.source === "gmail_read_only" && " · Gmail"}
                  </div>
                </div>
                <div className="md:text-right">
                  <div className="font-mono text-sm text-white">{fmtINR(batch.total_valuation)}</div>
                  <div className={`text-[10px] uppercase tracking-wider ${
                    batch.status === "Applied" ? "text-emerald-300"
                      : batch.status === "Duplicate" ? "text-red-300"
                      : "text-amber-300"
                  }`}>{batch.status}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PDF Parse Result */}
      {pdfResult && pdfResult.schemes && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="pdf-result-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">
                {isMfCentral ? "CAS Review" : "Imported Mutual Funds"}
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-0.5">
                {pdfResult.holder_name && `${pdfResult.holder_name} | `}
                {pdfResult.owner_name && `${pdfResult.owner_name} portfolio | `}
                {pdfResult.statement_period && `Period: ${pdfResult.statement_period} | `}
                {pdfResult.records} schemes
                {pdfResult.folio_count > 0 && ` (${pdfResult.folio_count} folios`}
                {pdfResult.demat_mf_count > 0 && ` + ${pdfResult.demat_mf_count} demat)`}
              </div>
            </div>
            <div className="text-right">
              {isMfCentral && (
                <div className={`text-[10px] uppercase tracking-wider mb-1 ${
                  pdfResult.status === "Applied" ? "text-[var(--positive)]"
                    : pdfResult.status === "Duplicate" ? "text-[var(--negative)]"
                    : "text-amber-300"
                }`}>{pdfResult.status}</div>
              )}
              <div className="font-mono text-lg text-white">{fmtINR(pdfResult.total_valuation)}</div>
              <div className="flex gap-3 justify-end text-[10px] font-mono text-[var(--text-muted)]">
                {pdfResult.folio_valuation > 0 && <span>Folios: {fmtINR(pdfResult.folio_valuation)}</span>}
                {pdfResult.demat_mf_valuation > 0 && <span>Demat: {fmtINR(pdfResult.demat_mf_valuation)}</span>}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {pdfResult.schemes.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-base)] border border-white/5 rounded text-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white truncate">{s.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border flex-shrink-0 ${s.type === "demat" ? "bg-purple-500/10 text-purple-400 border-purple-500/20" : "bg-teal-500/10 text-teal-400 border-teal-500/20"}`}>
                      {s.type === "demat" ? "DEMAT" : "FOLIO"}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">
                    {s.units.toFixed(3)} units @ NAV {s.nav.toFixed(2)}
                  </div>
                </div>
                <div className="text-right ml-4 flex-shrink-0">
                  <div className="font-mono text-white">{fmtINR(s.value)}</div>
                  <div className={`font-mono text-[10px] ${s.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                    {s.pnl >= 0 ? "+" : ""}{fmtINR(s.pnl)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {isMfCentral && (
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
              <p className="text-xs text-[var(--text-muted)]">
                {pdfResult.status === "Ready for Review"
                  ? "Check the holder, statement period, scheme count and valuation before applying."
                  : pdfResult.status === "Applied"
                    ? "This CAS is now the approved imported mutual-fund portfolio."
                    : "This exact file was already received and cannot be applied twice."}
              </p>
              {pdfResult.status === "Ready for Review" && (
                <Button
                  onClick={handleApplyCas}
                  disabled={applyingCas}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black whitespace-nowrap"
                >
                  {applyingCas ? <Loader2 size={16} className="animate-spin mr-1" /> : <Check size={16} className="mr-1" />}
                  Apply to portfolio
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* US Stocks Import Result */}
      {usResult && usResult.stocks && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="us-result-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">
                Imported US Stocks
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-0.5">
                {usResult.account_name} | {usResult.records} stocks
                {usResult.cash_balance > 0 && ` | Cash: $${usResult.cash_balance.toLocaleString()}`}
                {usResult.dividend_ytd > 0 && ` | Div YTD: $${usResult.dividend_ytd.toFixed(2)}`}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-lg text-white">
                ${usResult.stocks.reduce((s, h) => s + h.value, 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {usResult.stocks.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-base)] border border-white/5 rounded text-sm">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-mono">{s.symbol}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">
                    {s.qty} shares @ ${s.price.toFixed(2)}
                  </div>
                </div>
                <div className="text-right ml-4 flex-shrink-0">
                  <div className="font-mono text-white">${s.value.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                  <div className={`font-mono text-[10px] ${s.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                    {s.pnl >= 0 ? "+" : ""}${s.pnl.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {usResult.dividends && usResult.dividends.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-muted)] font-semibold mb-2">Dividends Recorded</div>
              <div className="space-y-1">
                {usResult.dividends.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-emerald-500/5 border border-emerald-500/10 rounded text-sm">
                    <span className="font-mono text-emerald-400">{d.stock}</span>
                    <span className="text-xs text-[var(--text-muted)]">{d.date}</span>
                    <span className="font-mono text-emerald-400">${d.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import History */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="import-history">
        <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-3">Import History</div>
        {history.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No imports yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between p-3 bg-[var(--bg-base)] border border-white/5 rounded">
                <div className="flex items-center gap-3">
                  {h.type === "pdf_upload" ? <FileText size={16} className="text-[var(--brand)]" /> : <FileSpreadsheet size={16} className="text-[var(--text-muted)]" />}
                  <div>
                    <div className="text-sm text-white">{h.file_name}</div>
                    <div className="text-[10px] font-mono text-[var(--text-muted)]">
                      {h.source} | {h.mode} | {h.records} records
                      {h.holder_name && ` | ${h.holder_name}`}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs ${h.status === "success" ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{h.status}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">{new Date(h.timestamp).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtINR(v) {
  if (!v && v !== 0) return "--";
  const abs = Math.abs(v);
  if (abs >= 10000000) return `${v < 0 ? "-" : ""}₹${(Math.abs(v)/10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${v < 0 ? "-" : ""}₹${(Math.abs(v)/100000).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
